(function (root, factory) {
  const api = factory();

  if (typeof module === 'object' && module.exports) {
    module.exports = api;
  }

  if (root) {
    root.NamycoDesignCatalog = api;
  }
})(typeof window !== 'undefined' ? window : globalThis, function () {
  'use strict';

  const DESIGN_STORAGE_KEY = 'namyco-selected-design';

  function normalizeSearchText(value) {
    return String(value || '')
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLowerCase()
      .replace(/\s+/g, ' ')
      .trim();
  }

  function parseDesignLabel(value) {
    const text = String(value || '').replace(/\s+/g, ' ').trim();
    const match = text.match(/^design\s*(\d+)\b\s*(.*)$/i);

    if (!match) return null;

    const name = match[2]
      .replace(/^[;:–—-]\s*/, '')
      .replace(/[.;]\s*$/, '')
      .trim();

    return {
      number: match[1],
      name,
    };
  }

  function getVisibleDesigns(designs, query, limit) {
    const normalizedQuery = normalizeSearchText(query);
    const safeLimit = Math.max(0, Number(limit) || 0);
    const matches = normalizedQuery
      ? designs.filter(function (design) {
          return normalizeSearchText(design.number + ' ' + design.name).includes(normalizedQuery);
        })
      : designs.slice();

    return {
      matches,
      visible: matches.slice(0, safeLimit),
      hasMore: matches.length > safeLimit,
    };
  }

  function previousNode(node, rootNode) {
    if (!node || node === rootNode) return null;

    if (node.previousSibling) {
      node = node.previousSibling;
      while (node.lastChild) node = node.lastChild;
      return node;
    }

    return node.parentNode && node.parentNode !== rootNode ? node.parentNode : null;
  }

  function findDesignLabel(image, rootNode) {
    let node = image;

    for (let step = 0; step < 40; step += 1) {
      node = previousNode(node, rootNode);
      if (!node) break;

      if (node.nodeType === 3) {
        const parsedText = parseDesignLabel(node.textContent);
        if (parsedText) return parsedText;
      }

      if (node.nodeType === 1 && node.tagName !== 'IMG') {
        const parsedElement = parseDesignLabel(node.textContent);
        if (parsedElement) return parsedElement;
      }
    }

    return null;
  }

  function collectDesigns(fragment) {
    const designs = [];
    const seenNumbers = new Set();

    fragment.querySelectorAll('img').forEach(function (image) {
      const source = image.getAttribute('src') || image.getAttribute('data-src');
      if (!source) return;

      const label = findDesignLabel(image, fragment);
      if (!label || seenNumbers.has(label.number)) return;

      seenNumbers.add(label.number);
      designs.push({
        number: label.number,
        name: label.name,
        source,
        sourceSet: image.getAttribute('srcset') || '',
      });
    });

    return designs;
  }

  function saveSelectedDesign(storage, design) {
    if (!storage || !design || !/^\d+$/.test(String(design.number || ''))) return false;

    storage.setItem(
      DESIGN_STORAGE_KEY,
      JSON.stringify({
        number: String(design.number),
        name: String(design.name || ''),
        source: String(design.source || ''),
      })
    );
    return true;
  }

  function loadSelectedDesign(storage) {
    if (!storage) return null;

    try {
      const selected = JSON.parse(storage.getItem(DESIGN_STORAGE_KEY));
      if (!selected || !/^\d+$/.test(String(selected.number || ''))) return null;

      const result = {
        number: String(selected.number),
        name: String(selected.name || ''),
      };
      if (selected.source) result.source = String(selected.source);
      return result;
    } catch (_error) {
      return null;
    }
  }

  function clearSelectedDesign(storage) {
    if (!storage) return false;
    storage.removeItem(DESIGN_STORAGE_KEY);
    return true;
  }

  function renderSelectedDesignConfirmation(rootElement, selected) {
    if (!rootElement || !selected || !selected.number) return false;

    const image = rootElement.querySelector('[data-selected-design-image]');
    const title = rootElement.querySelector('[data-selected-design-title]');
    const name = rootElement.querySelector('[data-selected-design-name]');

    if (image && selected.source) {
      image.src = selected.source;
      image.alt = selected.name
        ? 'Design ' + selected.number + ' — ' + selected.name
        : 'Design ' + selected.number;
      image.hidden = false;
    }
    if (title) title.textContent = 'Design ' + selected.number + ' sélectionné ✓';
    if (name) name.textContent = selected.name || 'Design brodé';
    rootElement.hidden = false;
    return true;
  }

  function findDesignInput(rootNode) {
    if (!rootNode || !rootNode.querySelectorAll) return null;

    const labels = rootNode.querySelectorAll('#infiniteoptions-container label, #infiniteoptions-container .spb-label');
    for (const label of labels) {
      if (!normalizeSearchText(label.textContent).includes('choix du design')) continue;

      const targetId = label.getAttribute('for');
      if (targetId) {
        const target = rootNode.getElementById
          ? rootNode.getElementById(targetId)
          : rootNode.querySelector('#' + targetId);
        if (target) return target;
      }

      const nested = label.querySelector('input, textarea, select');
      if (nested) return nested;

      const group = label.closest('.spb-productoptionswatch, .spb-select, .spb-textinput, .spb-textarea, div');
      const nearby = group && group.querySelector('input, textarea, select');
      if (nearby) return nearby;
    }

    return null;
  }

  function fillSelectedDesign(rootNode, selected) {
    const input = findDesignInput(rootNode);
    if (!input || !selected || !selected.number || String(input.value || '').trim()) return false;

    input.value = String(selected.number);
    const view = input.ownerDocument && input.ownerDocument.defaultView;
    const EventClass = view ? view.Event : Event;
    input.dispatchEvent(new EventClass('input', { bubbles: true }));
    input.dispatchEvent(new EventClass('change', { bubbles: true }));
    return true;
  }

  function createCard(design, documentNode, onSelect) {
    const doc = documentNode || document;
    const card = doc.createElement('article');
    card.className = 'namyco-design-card';

    const imageWrapper = doc.createElement('div');
    imageWrapper.className = 'namyco-design-card__image';

    const image = doc.createElement('img');
    image.src = design.source;
    if (design.sourceSet) image.srcset = design.sourceSet;
    image.loading = 'lazy';
    image.decoding = 'async';
    image.alt = design.name
      ? 'Design ' + design.number + ' — ' + design.name
      : 'Design ' + design.number;

    const content = doc.createElement('div');
    content.className = 'namyco-design-card__content';

    const number = doc.createElement('p');
    number.className = 'namyco-design-card__number';
    number.textContent = 'Design ' + design.number;

    const name = doc.createElement('p');
    name.className = 'namyco-design-card__name';
    name.textContent = design.name || 'Design brodé';

    const selectButton = doc.createElement('button');
    selectButton.className = 'button button--secondary namyco-design-card__select';
    selectButton.type = 'button';
    selectButton.setAttribute('data-select-design', '');
    selectButton.textContent = 'Choisir ce design';
    selectButton.addEventListener('click', function () {
      if (typeof onSelect === 'function') onSelect(design, selectButton);
    });

    imageWrapper.appendChild(image);
    content.append(number, name, selectButton);
    card.append(imageWrapper, content);

    return card;
  }

  function initializeCatalog(rootElement) {
    const template = rootElement.querySelector('[data-design-source]');
    const input = rootElement.querySelector('[data-design-search]');
    const grid = rootElement.querySelector('[data-design-grid]');
    const status = rootElement.querySelector('[data-design-status]');
    const loadMore = rootElement.querySelector('[data-design-more]');
    const empty = rootElement.querySelector('[data-design-empty]');
    const selectionStatus = rootElement.querySelector('[data-design-selection-status]');

    if (!template || !input || !grid || !status || !loadMore || !empty || !selectionStatus) return;

    const designs = collectDesigns(template.content);
    let visibleLimit = 24;

    function selectDesign(design, button) {
      if (!saveSelectedDesign(window.localStorage, design)) return;

      selectionStatus.hidden = false;
      selectionStatus.textContent = 'Design ' + design.number + ' sélectionné ✓ On te dirige vers les vêtements…';
      button.textContent = 'Design ' + design.number + ' sélectionné ✓';
      button.disabled = true;

      window.setTimeout(function () {
        window.location.assign('/collections/all?design=' + encodeURIComponent(design.number));
      }, 700);
    }

    function render() {
      const result = getVisibleDesigns(designs, input.value, visibleLimit);
      grid.replaceChildren.apply(
        grid,
        result.visible.map(function (design) {
          return createCard(design, document, selectDesign);
        })
      );

      const count = result.matches.length;
      status.textContent = count + (count === 1 ? ' design trouvé' : ' designs trouvés');
      empty.hidden = count !== 0;
      loadMore.hidden = !result.hasMore;
      rootElement.classList.add('is-enhanced');
    }

    input.addEventListener('input', function () {
      visibleLimit = 24;
      render();
    });

    loadMore.addEventListener('click', function () {
      visibleLimit += 24;
      render();
    });

    render();
  }

  function initializeSelectedDesignOnProduct() {
    const confirmation = document.querySelector('[data-selected-design-confirmation]');
    const selected = loadSelectedDesign(window.localStorage);
    if (!confirmation || !selected) return;

    function showConfirmation() {
      renderSelectedDesignConfirmation(confirmation, selected);
    }

    function attemptFill() {
      const input = findDesignInput(document);
      if (!input) return false;

      if (fillSelectedDesign(document, selected) || String(input.value || '').trim() === selected.number) {
        showConfirmation();
      }
      return true;
    }

    if (attemptFill()) return;

    const observer = new MutationObserver(function () {
      if (attemptFill()) observer.disconnect();
    });
    observer.observe(document.body, { childList: true, subtree: true });
    window.setTimeout(function () {
      observer.disconnect();
    }, 15000);
  }

  function initializeAllCatalogs() {
    document.querySelectorAll('[data-namyco-design-catalog]').forEach(initializeCatalog);
  }

  function shouldShowGarmentChooser(pathname, selected) {
    const normalizedPath = String(pathname || '').replace(/\/+$/, '');
    return normalizedPath === '/collections/all' && Boolean(selected && selected.number);
  }

  function initializeGarmentChooser() {
    const chooser = document.querySelector('[data-namyco-garment-chooser]');
    const standardCollection = document.querySelector('[data-standard-collection]');
    if (!chooser || !standardCollection) return;

    const selected = loadSelectedDesign(window.localStorage);
    if (!shouldShowGarmentChooser(window.location.pathname, selected)) return;

    const status = chooser.querySelector('[data-garment-selection-status]');
    const clearSelection = chooser.querySelector('[data-clear-design-selection]');
    if (status) {
      status.textContent = 'Design ' + selected.number + ' sélectionné ✓ Choisis maintenant ton vêtement.';
    }
    if (clearSelection) {
      clearSelection.addEventListener('click', function () {
        clearSelectedDesign(window.localStorage);
      });
    }

    const collectionBanner = document.querySelector('.collection-hero');
    if (collectionBanner) collectionBanner.hidden = true;
    standardCollection.hidden = true;
    chooser.hidden = false;
  }

  function initializeNamycoDesignFeatures() {
    initializeAllCatalogs();
    initializeSelectedDesignOnProduct();
    initializeGarmentChooser();
  }

  if (typeof document !== 'undefined') {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', initializeNamycoDesignFeatures);
    } else {
      initializeNamycoDesignFeatures();
    }
  }

  return {
    parseDesignLabel,
    normalizeSearchText,
    getVisibleDesigns,
    collectDesigns,
    saveSelectedDesign,
    loadSelectedDesign,
    clearSelectedDesign,
    renderSelectedDesignConfirmation,
    findDesignInput,
    fillSelectedDesign,
    createCard,
    shouldShowGarmentChooser,
  };
});
